/**
 * Mapbox `road` タイル feature 群から「経路スナップ先になる車道中心線」を抽出する
 * 純ロジック。取得は services/mapbox-footprint.ts (fetchMapboxRoadCenterlines)。
 *
 * 分離歩道・横断歩道 (class=path) は**除外**する — それ自体がスナップ元のノイズ源
 * (route-snap.ts 参照)。自動車専用・鉄道等の非歩行クラスもスナップ先にしない
 * (矢印が高速道路の中心に吸われるのを防ぐ)。
 */
import type { TileRoadFeature } from './bridge-detect';

/**
 * スナップ先として認める road class = **印刷スタイルが白い帯として描く道路だけ**。
 *
 * service (敷地内通路・駐車場アクセス) や track は applyEvacuationStyle の roadMinor
 * (≈背景色) でほぼ見えないため、そこへスナップすると「何も描かれていない場所を
 * 矢印が走る」見た目になる (実測: 阪本小 — 描画照会で route 画素に道路レイヤーなし)。
 * 矢印は読者に見える道路の上だけを通す。path (歩道・横断歩道) も従来どおり除外。
 */
const SNAPPABLE_ROAD_CLASS =
  /^(trunk|primary|secondary|tertiary|street|street_limited|pedestrian)(_link)?$/;

/** feature 群からスナップ先中心線 (LineString 座標列) を抽出する。 */
export function roadCenterlinesFromFeatures(
  features: TileRoadFeature[],
): Array<Array<[number, number]>> {
  const lines: Array<Array<[number, number]>> = [];
  for (const f of features) {
    const cls = f.properties?.class ?? '';
    if (!SNAPPABLE_ROAD_CLASS.test(cls)) continue;
    const g = f.geometry;
    if (g.type === 'LineString') {
      lines.push(g.coordinates as Array<[number, number]>);
    } else if (g.type === 'MultiLineString') {
      lines.push(...(g.coordinates as Array<Array<[number, number]>>));
    }
  }
  return lines;
}
